import type { Company } from "@repo/app-types/entities/company";
import type {
  ProjectUpdate,
  ProjectWithInterviewCount,
} from "@repo/app-types/entities/project";
import { getInterviewLinkForProject } from "@repo/app-types/entities/project";
import { ProjectDetailsTabPath } from "@repo/app-types/helpers/adminAppRoutes";
import { splitProjectQuestions } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Label } from "@repo/ui/Label/label";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { Size } from "@repo/ui/helpers/helpers";
import {
  IconCircleCheck,
  IconEye,
  IconPlus,
  IconUsersGroup,
} from "@tabler/icons-react";
import React, { useEffect, useState, type FC } from "react";
import { Button as AriaButton, MenuTrigger } from "react-aria-components";
import { getAxiosInstanceWithAuth } from "../../api/axiosConfig.js";
import { useAppDispatch, useAppSelector } from "../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";
import { TargetingSlideoverContainer } from "../targeting/targetingSlideoverContainer.js";
import { TargetingSlideoverVariant } from "../targeting/targetingSlideoverTypes.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import { AppearanceSettingsSection } from "./projectSettings/appearanceSettingsSection.js";
import { IncentiveSettingsSection } from "./projectSettings/incentiveSettingsSection.js";
import { InterviewLinkCopybox } from "./projectSettings/interviewLinkCopybox.js";
import { OtherSettingsSection } from "./projectSettings/otherSettingsSection.js";
import { ProjectNotificationsSettingsSection } from "./projectSettings/projectNotificationsSettingsSection.js";
import { ProjectSettingsQuestionsSection } from "./surveyQuestions/projectSettingsQuestionSection.js";

const SurveyModeProjectSettingsTabModal = {
  REGENERATE_INTERVIEW_LINK: "REGENERATE_INTERVIEW_LINK",
  REMOVE_SURVEY: "REMOVE_SURVEY",
} as const;
type SurveyModeProjectSettingsTabModal =
  (typeof SurveyModeProjectSettingsTabModal)[keyof typeof SurveyModeProjectSettingsTabModal];

interface SurveyModeProjectSettingsProps {
  project: ProjectWithInterviewCount;
  setCurrentTab: (tab: ProjectDetailsTabPath) => void;
  interviewLimitSettingRef: React.MutableRefObject<HTMLLabelElement | null>;
  onCopyInterviewLink: () => void;
  onSaveProject: (changes: ProjectUpdate) => Promise<void>;
  company: Company;
}

export const SurveyModeProjectSettings: FC<SurveyModeProjectSettingsProps> = ({
  project,
  company,
  setCurrentTab,
  interviewLimitSettingRef,
  onCopyInterviewLink,
  onSaveProject,
}) => {
  const dispatch = useAppDispatch();

  // Store the source the user selects to add more participants from
  const [targetingSource, setTargetingSource] = React.useState<
    TargetingSlideoverVariant | undefined
  >();

  const teammate = useAppSelector(selectTeammate);
  const [demoInterviewLink, setDemoInterviewLink] = useState<string | null>(
    null,
  );

  const { complexQuestions, basicQuestions } = splitProjectQuestions(
    project.questions,
  );

  // Data loading on initial render
  useEffect(() => {
    // Retrieve the demo interview link for this project
    const getDemoInterviewLink = async () => {
      try {
        const axios = await getAxiosInstanceWithAuth();
        const { data } = await axios.get(
          `/projects/${project.id}/demo-interview-link`,
        );

        setDemoInterviewLink(data.link);
      } catch (err) {
        // Do nothing.
      }
    };

    getDemoInterviewLink();
  }, []);

  const inviteLinkUrl = getInterviewLinkForProject(
    import.meta.env.VITE_INTERVIEW_APP_BASE_URL,
    company,
    project,
  );

  const [isSendingTestInvitationEmail, setIsSendingTestInvitationEmail] =
    useState(false);

  const renderAddParticipantsButton = () => {
    return (
      <div className="relative">
        <MenuTrigger>
          <AriaButton className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-foreground shadow-xs ring-1 ring-border-strong ring-inset hover:bg-gray-50 disabled:bg-disabled disabled:text-muted-foreground disabled:shadow-none">
            <IconPlus className="mr-1 h-5 w-5" />
            Add participants
          </AriaButton>
          <SimpleMenuPopover>
            <SimpleMenu aria-label="Add participants">
              <SimpleMenuItem
                onAction={() => {
                  setTargetingSource(TargetingSlideoverVariant.CSV);
                }}
              >
                Add via CSV
              </SimpleMenuItem>
              <SimpleMenuItem
                onAction={() => {
                  setTargetingSource(TargetingSlideoverVariant.MANUAL);
                }}
              >
                Add manually
              </SimpleMenuItem>
              <SimpleMenuItem
                onAction={() =>
                  window.open(
                    "https://help.alpharun.com/en/articles/1041345",
                    "_blank",
                  )
                }
              >
                Add via API/Zapier
              </SimpleMenuItem>
            </SimpleMenu>
          </SimpleMenuPopover>
        </MenuTrigger>
      </div>
    );
  };

  const maybeRenderProgressBanners = () => {
    // If the project is live, outreach is in progress for all pending interviews.
    const shouldShowOutreachInProgressPill =
      project.is_live && Boolean(project.pending_interview_count);

    const interviewCount = project.interview_count;

    if (!interviewCount && !shouldShowOutreachInProgressPill) return null;

    return (
      <div className="flex space-x-2">
        {shouldShowOutreachInProgressPill ? (
          <button
            className="flex w-fit cursor-pointer items-center justify-center space-x-2 rounded-md border border-solid border-sky-600 bg-sky-50 px-1 py-0.5 pl-2 hover:bg-sky-100"
            onClick={() => {
              setTargetingSource(
                TargetingSlideoverVariant.OUTREACH_PARTICIPANTS,
              );
            }}
          >
            <div className="relative flex h-3 w-3">
              <span
                className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-800 opacity-75"
                style={{ animationDuration: "2s" }}
              />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-sky-800" />
            </div>
            <span className="text-sm font-medium text-sky-700">
              {`Outreach in progress for ${
                project.pending_interview_count
              } interview${project.pending_interview_count > 1 ? "s" : ""}`}
            </span>
          </button>
        ) : null}
        {Boolean(interviewCount) && (
          <button
            className="flex w-fit cursor-pointer space-x-2 rounded-md border border-solid border-green-600 bg-green-50 px-1 py-0.5 hover:bg-green-100"
            onClick={() => {
              setCurrentTab(ProjectDetailsTabPath.Interviews);
            }}
          >
            <IconCircleCheck className="h-5 w-5 text-green-700" />
            <span className="text-sm font-medium text-green-700">
              {`Insights available for ${interviewCount} interview${
                interviewCount > 1 ? "s" : ""
              }`}
            </span>
          </button>
        )}
      </div>
    );
  };

  const renderParticipantsSummary = () => {
    const shouldShowPendingParticipantsPill =
      !project.is_live && Boolean(project.pending_interview_count);

    // The project is paused if it's not live and there were some non-empty interviews
    const isPaused = !project.is_live && project.interview_count > 0;

    const getDescriptionText = () => {
      if (project.is_live)
        return "Participants you add will be emailed for interviews. ";

      return `When you ${
        isPaused ? "restart" : "launch"
      } this project, we'll email these participants for interviews. `;
    };

    return (
      <>
        <div className="flex space-x-2">
          {shouldShowPendingParticipantsPill ? (
            <Button
              variant={ButtonVariant.Secondary}
              icon={<IconUsersGroup className="h-5 w-5" />}
              onClick={() => {
                setTargetingSource(
                  TargetingSlideoverVariant.PENDING_PARTICIPANTS,
                );
              }}
            >
              {`${project.pending_interview_count} pending ${
                project.pending_interview_count > 1
                  ? "participants"
                  : "participant"
              }`}
            </Button>
          ) : null}
          {renderAddParticipantsButton()}
        </div>
        <div className="mt-2 text-sm text-muted-foreground">
          <span>{getDescriptionText()}</span>
          <span
            className="cursor-pointer font-semibold"
            onClick={onSendTestInviteEmailClick}
          >
            Click here
          </span>
          <span> to send yourself a test invitation email.</span>
          {isSendingTestInvitationEmail ? (
            <Loader style={LoaderStyle.SPINNER} />
          ) : null}
        </div>
      </>
    );
  };

  const onSendTestInviteEmailClick = async () => {
    // If a send is already in progress, don't trigger another.
    if (isSendingTestInvitationEmail) return;

    setIsSendingTestInvitationEmail(true);
    try {
      const axios = await getAxiosInstanceWithAuth();
      await axios.post(`/projects/${project.id}/send-test-invite`);

      showNotification(dispatch, {
        id: `project-${
          project.id
        }-testinvite-succeeded-${new Date().getTime()}`,
        primaryMessage: `Test invitation email sent to ${teammate?.email}`,
        type: NotificationType.SUCCESS,
      });
      setIsSendingTestInvitationEmail(false);
    } catch (err) {
      console.error("error sending test invitation email", err);
      setIsSendingTestInvitationEmail(false);
      showNotification(dispatch, {
        id: `project-${project.id}-testinvite-failed-${new Date().getTime()}`,
        primaryMessage: `Failed to send test invitation email`,
        type: NotificationType.FAILURE,
      });
    }
  };

  return (
    <>
      {maybeRenderProgressBanners()}

      <div>
        <Label size={Size.LARGE}>Questions</Label>
        <ProjectSettingsQuestionsSection
          basicQuestions={basicQuestions}
          complexQuestions={complexQuestions}
          project={project}
          company={company}
          onSaveProject={onSaveProject}
        />
      </div>

      {basicQuestions.length > 0 || complexQuestions.length > 0 ? (
        <div>
          <Button
            variant={ButtonVariant.Primary}
            icon={<IconEye className="h-5 w-5" />}
            onClick={() => {
              if (demoInterviewLink) window.open(demoInterviewLink, "_blank");
            }}
          >
            Try this AI interview
          </Button>
        </div>
      ) : null}
      <div>
        <div className="flex items-center justify-between">
          <Label size={Size.LARGE}>Interview participants</Label>
        </div>
        <InterviewLinkCopybox
          className="mb-2"
          linkLabel="Interview link"
          inviteLinkUrl={inviteLinkUrl}
          project={project}
          onCopyInterviewLink={onCopyInterviewLink}
        />
        <div className="mb-2 flex flex-col justify-between">
          <Label size={Size.SMALL} className="mt-2">
            You can also add participants that we should email:
          </Label>
        </div>
        {renderParticipantsSummary()}

        {targetingSource ? (
          <TargetingSlideoverContainer
            project={project}
            variant={targetingSource}
            onClose={() => {
              setTargetingSource(undefined);
            }}
          />
        ) : null}
      </div>
      <IncentiveSettingsSection
        incentiveQuantity={project.incentive_quantity}
        isIncentiveCharityOnly={project.settings.is_incentive_charity_only}
        onIncentiveQuantityChange={(newQuantity) => {
          onSaveProject({ incentive_quantity: newQuantity });
        }}
        onIncentiveCharityOnlyChange={(isIncentiveCharityOnly) => {
          onSaveProject({
            settings: {
              ...project.settings,
              is_incentive_charity_only: isIncentiveCharityOnly,
            },
          });
        }}
      />
      <ProjectNotificationsSettingsSection
        project={project}
        onSaveProject={onSaveProject}
        isTrainingProject={false}
      />
      <AppearanceSettingsSection
        onSaveProject={onSaveProject}
        company={company}
        project={project}
      />
      <OtherSettingsSection
        onSaveProject={onSaveProject}
        company={company}
        project={project}
        interviewLimitSettingRef={interviewLimitSettingRef}
      />
    </>
  );
};
